import React, { useState, useEffect, useCallback } from "react";
import axiosBase from "axios";
import { GoogleMap, Marker, DirectionsRenderer, useJsApiLoader } from '@react-google-maps/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import MarketingQRModal from "./marketing/MarketingQRModal";
import RouteOptimizerModal from "./admin/RouteOptimizerModal";
import PendingApprovalsModal from "./admin/PendingApprovalsModal";
import AutoApprovedQuotesModal from "./admin/AutoApprovedQuotesModal";
import { FilterProvider } from "./admin/FilterContext";
import { CreditCard, CalendarDays, FastForward, Truck, CheckCircle2, BookOpen, FileText, Fuel, Mail, Camera, Download, Map as MapIcon, QrCode, Star, MessageSquare } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import PaymentRemindersModal from "./admin/PaymentRemindersModal";
import BinModal from "./admin/BinModal";
import CalendarModal from "./admin/CalendarModal";
import AllJobsModal from "./admin/AllJobsModal";
import EmailCenterModal from "./admin/EmailCenterModal";
import PhotoGalleryModal from "./admin/PhotoGalleryModal";
import ReviewsModal from "./admin/ReviewsModal";
import InvoicesModal from "./admin/InvoicesModal";
import VenmoPaidWatcher from "./admin/VenmoPaidWatcher";
import ChimeToggle from "./admin/ChimeToggle";
import PricingSettingsModal from "./admin/PricingSettingsModal";
import EmailPreviewModal from "./admin/EmailPreviewModal";
import QuickActionsGrid from "./admin/QuickActionsGrid";
import JobBinsGrid from "./admin/JobBinsGrid";
import CompletionPhotoModal from "./admin/CompletionPhotoModal";
import useGalleryReel from "./admin/useGalleryReel";
import useAdminCalendar from "./admin/useAdminCalendar";
import useRoutePlanner from "./admin/useRoutePlanner";
import useEmailCenter from "./admin/useEmailCenter";
import useSmsCenter from "./admin/useSmsCenter";
import { getDaysInMonth, getFirstDayOfWeek, formatCalendarDate, formatPrice, formatTime, getStartOfWeek } from "./admin/dashboardUtils";
import SmsTestModal from "./admin/SmsTestModal";
import { toast } from "../lib/toast";
import { logger } from "../utils/logger";


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// --- Job-status presentation helpers (kept flat to avoid nested ternaries) ---
const STATUS_BORDER_CLASS = {
  completed: "border-green-300 bg-green-50",
  in_progress: "border-yellow-300 bg-yellow-50",
};
const STATUS_BADGE_CLASS = {
  completed: "bg-green-500",
  in_progress: "bg-yellow-500",
};
const STATUS_BADGE_LABEL = {
  completed: "✓ Completed",
  in_progress: "⏳ In Progress",
};
const jobBorderClass = (status) => STATUS_BORDER_CLASS[status] || "border-blue-300 bg-blue-50";
const jobBadgeClass = (status) => STATUS_BADGE_CLASS[status] || "bg-blue-500";
const jobBadgeLabel = (status) => STATUS_BADGE_LABEL[status] || "📅 Scheduled";

// Local axios instance — admin requests need the httpOnly admin_session
// cookie. Using a per-module instance instead of `axios.defaults` so we
// DON'T pollute customer-side requests with credentials (which would force
// a CORS preflight whose `Access-Control-Allow-Origin: *` response is
// rejected by browsers when credentials are present — see CORS spec).
const axios = axiosBase.create({ withCredentials: true });

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ""; // Set this in .env file

const AdminDashboard = ({ adminDisplayName = "Admin", onLogout }) => {
  // ----- Global 401 interceptor: surface a true session expiry once, then
  // hand control back to the parent which routes to /admin/login.
  //
  // Guards against false-positives:
  //   1. Skip /admin/login itself (wrong-password 401 != expired session)
  //   2. Require at least one prior successful admin call (rules out the
  //      cold-start race where a stray non-credentialed admin axios call
  //      fires before login finishes)
  //   3. 3-second grace window after mount to let cookies settle
  React.useEffect(() => {
    const mountedAt = Date.now();
    const state = { hadSuccess: false };

    const successId = axios.interceptors.response.use(
      (res) => {
        const url = res?.config?.url || "";
        if (url.includes("/admin/") && !url.includes("/admin/login")) {
          state.hadSuccess = true;
        }
        return res;
      },
      (err) => Promise.reject(err)
    );

    const errorId = axios.interceptors.response.use(
      (res) => res,
      (error) => {
        const status = error?.response?.status;
        const url = error?.config?.url || "";
        const isAdminUrl = url.includes("/admin/") && !url.includes("/admin/login");
        const gracePeriodOver = Date.now() - mountedAt > 3000;
        if (status === 401 && isAdminUrl && gracePeriodOver && state.hadSuccess) {
          if (!window.__t2t_session_expired_shown) {
            window.__t2t_session_expired_shown = true;
            try {
              alert("Your admin session has expired. Please log in again.");
            } catch (e) {
              // running in a non-window env (SSR/test); session expiry will
              // still trigger via onLogout below, just without the alert UI.
              logger.debug("session-expired alert suppressed:", e);
            }
            setTimeout(() => { window.__t2t_session_expired_shown = false; }, 1500);
          }
          if (typeof onLogout === "function") onLogout();
        }
        return Promise.reject(error);
      }
    );
    return () => {
      axios.interceptors.response.eject(successId);
      axios.interceptors.response.eject(errorId);
    };
  }, [onLogout]);

  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [dailyBookings, setDailyBookings] = useState([]);
  const [weeklySchedule, setWeeklySchedule] = useState({});
  const [loading, setLoading] = useState(false);
  const [mapCenter, setMapCenter] = useState({ lat: 40.7128, lng: -74.0060 }); // NYC default
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);
  const [showReviewsModal, setShowReviewsModal] = useState(false);
  const [showSmsTestModal, setShowSmsTestModal] = useState(false);
  const [completionPhoto, setCompletionPhoto] = useState(null);
  const [completionNote, setCompletionNote] = useState("");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [selectedBin, setSelectedBin] = useState(null);
  const [binBookings, setBinBookings] = useState([]);
  const [pendingQuotes, setPendingQuotes] = useState([]);
  const [showQuoteApproval, setShowQuoteApproval] = useState(false);
  const [approvalStats, setApprovalStats] = useState({});
  // Auto-approved quotes review (separate from pending-approval queue)
  const [autoApprovedQuotes, setAutoApprovedQuotes] = useState([]);
  const [showAutoApprovedQuotes, setShowAutoApprovedQuotes] = useState(false);
  const [autoApprovedLoading, setAutoApprovedLoading] = useState(false);
  const [showSmsCenter, setShowSmsCenter] = useState(false);
  const [showAllJobsModal, setShowAllJobsModal] = useState(false);
  const [showInvoicesModal, setShowInvoicesModal] = useState(false);
  const [showPricingSettings, setShowPricingSettings] = useState(false);
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [allJobs, setAllJobs] = useState([]);
  const [pendingPayments, setPendingPayments] = useState([]);  // New state for unpaid bookings
  const [showPendingPayments, setShowPendingPayments] = useState(false);  // Modal state
  
  // Collapse states for all sections
  const [collapsed, setCollapsed] = useState({
    quickActions: false,
    dailySchedule: false,
    weeklyOverview: false,
    quotesApproval: false,
    jobBins: false,
    calendar: false,
    smsCenter: false,
    gallery: false
  });
  
  const toggleSection = (section) => {
    setCollapsed(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  
  // Photo Management States
  const [showPhotoGallery, setShowPhotoGallery] = useState(false);
  const { galleryPhotos, reelPhotos, uploadingGalleryPhoto, fetchGalleryPhotos, fetchReelPhotos, uploadGalleryPhoto, updateReelPhoto, removeGalleryPhoto, cleaningBroken, cleanupBrokenPhotos } = useGalleryReel();
  
  // Customer Photo Viewing States
  const [showCustomerPhoto, setShowCustomerPhoto] = useState(false);
  const [currentCustomerPhoto, setCurrentCustomerPhoto] = useState(null);

  const [failedQuoteImages, setFailedQuoteImages] = useState(new Set());
  
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: ['geometry'],
    onLoad: () => {},
    onError: () => toast.error('Google Maps failed to load')
  });

  // IMPORTANT: All useCallback functions must be defined BEFORE useEffect hooks that reference them
  // Photo Management Functions

  // Load data on component mount and when date changes

  const fetchDailySchedule = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/daily-schedule?date=${selectedDate}`);
      setDailyBookings(response.data);
      
      // Set map center to first booking location if available
      if (response.data.length > 0 && response.data[0].address) {
        geocodeAddress(response.data[0].address);
      }
    } catch (error) {
      toast.error("Failed to fetch daily schedule");
    }
    setLoading(false);
  }, [selectedDate]);

  const { showCalendar, setShowCalendar, calendarData, setCalendarData, selectedCalendarDate, setSelectedCalendarDate, showDateJobsModal, setShowDateJobsModal, currentMonth, setCurrentMonth, fetchCalendarData, openCalendar, closeCalendar, changeMonth } = useAdminCalendar();
  const { directions, setDirections, optimizedRoute, setOptimizedRoute, showRouteModal, setShowRouteModal, selectedRouteBooking, setSelectedRouteBooking, routeDirections, setRouteDirections, calculateOptimalRoute, startRoute, closeRouteModal } = useRoutePlanner({ dailyBookings, isLoaded });
  const { emailCompose, setEmailCompose, sendingEmail, setSendingEmail, exportJobContacts, sendBulkEmailReminder, sendBookingConfirmationEmail, sendPaymentReminder, sendCustomEmail } = useEmailCenter();
  const { smsMessages, setSmsMessages, smsLoading, setSmsLoading, newSmsMessage, setNewSmsMessage, selectedCustomerPhone, setSelectedCustomerPhone, fetchSmsMessages, sendSmsMessage } = useSmsCenter();

  const fetchWeeklySchedule = useCallback(async () => {
    try {
      const startOfWeek = getStartOfWeek(new Date(selectedDate));
      const response = await axios.get(`${API}/admin/weekly-schedule?start_date=${startOfWeek}`);
      setWeeklySchedule(response.data);
    } catch (error) {
      toast.error("Failed to fetch weekly schedule");
    }
  }, [selectedDate]);


  const geocodeAddress = async (address) => {
    if (!isLoaded || !window.google) return;
    
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const location = results[0].geometry.location;
        setMapCenter({ lat: location.lat(), lng: location.lng() });
      }
    });
  };


  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      await axios.patch(`${API}/admin/bookings/${bookingId}`, { status: newStatus });
      fetchDailySchedule(); // Refresh data
      // Also refresh the rolling-7-day Completed bin so yesterday's completions
      // (and any just-marked-complete) show up immediately.
      fetchRecentCompleted();
      toast.success("Booking status updated");
    } catch (error) {
      toast.error("Failed to update booking status");
    }
  };

  const handleCompleteWithPhoto = (booking) => {
    setSelectedBooking(booking);
    setShowCompletionModal(true);
    setCompletionNote("");
    setCompletionPhoto(null);
  };

  const handleCompletionPhotoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setCompletionPhoto(file);
    }
  };

  const handleViewCustomerPhoto = (booking) => {
    if (!booking.image_path) return;
    // Build the public image URL from the stored disk path. The path looks like
    // "/app/static/quote_images/quote_<uuid>.jpg" — we want the last two segments
    // (folder + filename). Earlier code hard-coded "booking_images" which was wrong
    // — the AI quote photos live in "quote_images".
    const backend_url = process.env.REACT_APP_BACKEND_URL;
    let photoUrl;
    if (booking.image_path.startsWith('http')) {
      photoUrl = booking.image_path;
    } else {
      const parts = booking.image_path.split('/').filter(Boolean);
      const folder = parts[parts.length - 2] || 'quote_images';
      const filename = parts[parts.length - 1];
      photoUrl = `${backend_url}/api/images/${folder}/${filename}`;
    }
    setCurrentCustomerPhoto({
      url: photoUrl,
      booking_id: booking.id,
      customer_phone: booking.phone,
      pickup_date: booking.pickup_date,
      address: booking.address
    });
    setShowCustomerPhoto(true);
  };

  const submitCompletion = async () => {
    if (!completionPhoto) {
      toast.error("Please select a completion photo");
      return;
    }

    setUploadingPhoto(true);
    try {
      // First mark as completed
      await updateBookingStatus(selectedBooking.id, 'completed');

      // Upload completion photo
      const formData = new FormData();
      formData.append('file', completionPhoto);
      formData.append('completion_note', completionNote);

      await axios.post(`${API}/admin/bookings/${selectedBooking.id}/completion`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success("Job completed with photo!");
      setShowCompletionModal(false);
      fetchDailySchedule();
    } catch (error) {
      toast.error("Failed to upload completion photo");
    }
    setUploadingPhoto(false);
  };

  const notifyCustomer = async (bookingId) => {
    try {
      await axios.post(`${API}/admin/bookings/${bookingId}/notify-customer`);
      toast.success("SMS sent to customer with completion photo!");
    } catch (error) {
      toast.error("Failed to send SMS to customer");
    }
  };

  const testSmsPhoto = async (bookingId) => {
    try {
      await axios.post(`${API}/admin/test-sms-photo/${bookingId}`);
      toast.success("SMS photo test completed!");
    } catch (error) {
      toast.error("SMS photo test failed");
    }
  };



  const testSmsSetup = async () => {
    try {
      const response = await axios.post(`${API}/admin/test-sms`);
      if (response.data.configured) {
        toast.success("SMS is configured and ready!");
      } else {
        toast.error("SMS setup incomplete. Check Twilio credentials.");
      }
    } catch (error) {
      toast.error("SMS test failed. Check configuration.");
    }
  };





  // Send custom email to customer

  // Open email center with pre-filled recipient
  const openEmailCenter = (email) => {
    setEmailCompose({ ...emailCompose, to: email });
    setShowSmsCenter(true);
  };

  // Fetch pending payment bookings
  const fetchPendingPayments = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/pending-payments`);
      setPendingPayments(response.data);
    } catch (error) {
      logger.error('Error fetching pending payments:', error);
    }
  }, []);

  // Mark booking as paid
  const markAsPaid = async (bookingId) => {
    try {
      await axios.post(`${API}/admin/bookings/${bookingId}/mark-paid`);
      toast.success("Booking marked as paid and added to calendar!");
      
      // Refresh data
      fetchDailySchedule();
      fetchPendingPayments();
    } catch (error) {
      toast.error("Failed to mark as paid");
      logger.error('Mark paid error:', error);
    }
  };

  const rejectPayment = async (bookingId) => {
    if (!window.confirm("Remove this booking from Pending Payment? This will cancel the booking.")) return;
    try {
      await axios.patch(`${API}/admin/bookings/${bookingId}`, { status: "cancelled" });
      toast.success("Booking removed from pending payments.");
      fetchPendingPayments();
    } catch (error) {
      toast.error("Failed to reject payment");
      logger.error('Reject payment error:', error);
    }
  };

  const rejectAllPendingPayments = async () => {
    if (!window.confirm(`Remove ALL ${pendingPayments.length} bookings from Pending Payment? This cannot be undone.`)) return;
    try {
      let rejected = 0;
      for (const booking of pendingPayments) {
        await axios.patch(`${API}/admin/bookings/${booking.id}`, { status: "cancelled" });
        rejected++;
      }
      toast.success(`${rejected} bookings removed from pending payments.`);
      fetchPendingPayments();
    } catch (error) {
      toast.error("Failed to reject some payments");
      fetchPendingPayments();
    }
  };



  // Fetch all jobs (history and present). Wrapped in useCallback so we can
  // include it in the mount + auto-refresh effects below — bins must reflect
  // every active booking regardless of the calendar date the admin is viewing.
  const fetchAllJobs = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/all-bookings`);
      setAllJobs(response.data || []);
    } catch (error) {
      logger.error('Failed to fetch all jobs:', error);
      toast.error('Failed to load jobs');
    }
  }, []);

  // Open All Jobs modal
  const openAllJobsModal = () => {
    setShowAllJobsModal(true);
    fetchAllJobs();
  };

  const [recentCompletedBookings, setRecentCompletedBookings] = useState([]);

  const fetchRecentCompleted = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/recent-completed?days=7`);
      setRecentCompletedBookings(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      logger.error('Failed to fetch recent completed jobs:', err);
      setRecentCompletedBookings([]);
    }
  }, []);

  // Categorize bookings into bins (date-independent — sources from ALL jobs
  // so "Upcoming"/"In Progress" don't silently disappear when the admin
  // navigates the calendar to a different day).
  const categorizBookings = () => {
    const today = new Date().toISOString().split('T')[0];

    const bins = {
      new: [],      // scheduled for today + past-due + pending customer approval
      upcoming: [], // scheduled for future dates
      inProgress: [], // currently in progress
      completed: []   // sourced separately via fetchRecentCompleted (7-day rolling window)
    };

    allJobs.forEach(booking => {
      const bookingDate = booking.pickup_date ? booking.pickup_date.split('T')[0] : '';

      if (booking.status === 'in_progress') {
        bins.inProgress.push(booking);
      } else if (booking.status === 'pending_customer_approval') {
        bins.new.push(booking); // approval-pending: surface as priority
      } else if (booking.status === 'scheduled' || booking.status === 'pending_payment') {
        // pending_payment = customer wants to pay-in-person / pay-after-job.
        // Still needs to appear in Today/Upcoming so the crew can work it.
        if (bookingDate === today) {
          bins.new.push(booking);
        } else if (bookingDate > today) {
          bins.upcoming.push(booking);
        } else {
          bins.new.push(booking); // past-due — bubble back to New so it isn't lost
        }
      }
    });

    bins.completed = recentCompletedBookings;
    return bins;
  };

  const openBin = (binType) => {
    const bins = categorizBookings();
    setBinBookings(bins[binType] || []);
    setSelectedBin(binType);
  };

  const closeBin = () => {
    setSelectedBin(null);
    setBinBookings([]);
  };

  const openJobDetails = (job) => {
    // Open the job in a bin modal with just this single job
    setBinBookings([job]);
    setSelectedBin('details');
  };










  const fetchPendingQuotes = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/pending-quotes`);
      setPendingQuotes(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      logger.error('Error fetching pending quotes:', error);
      setPendingQuotes([]);
      toast.error('Failed to load pending quotes');
    }
  }, []);

  const fetchAutoApprovedQuotes = useCallback(async () => {
    setAutoApprovedLoading(true);
    try {
      const response = await axios.get(`${API}/admin/auto-approved-quotes?limit=30`);
      setAutoApprovedQuotes(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      logger.error('Error fetching auto-approved quotes:', error);
      setAutoApprovedQuotes([]);
      toast.error('Failed to load auto-approved quotes');
    } finally {
      setAutoApprovedLoading(false);
    }
  }, []);

  const handleDismissAutoApproved = useCallback(async (quoteId) => {
    try {
      await axios.post(`${API}/admin/quotes/${quoteId}/dismiss`);
      // Optimistic update — drop it from view immediately, then reconcile.
      setAutoApprovedQuotes(prev => prev.filter(q => q.id !== quoteId));
      toast.success('Quote dismissed');
      fetchApprovalStats();
    } catch (err) {
      logger.error(err);
      toast.error('Could not dismiss quote');
    }
  }, []);

  const handleDismissAllAutoApproved = useCallback(async () => {
    if (!window.confirm('Clear all auto-approved quotes from this view? They\'ll stay in the database (All Jobs search still finds them).')) return;
    try {
      const res = await axios.post(`${API}/admin/quotes/dismiss-all-auto-approved`);
      setAutoApprovedQuotes([]);
      toast.success(`Cleared ${res.data?.dismissed || 0} auto-approved quotes`);
      fetchApprovalStats();
    } catch (err) {
      logger.error(err);
      toast.error('Could not clear auto-approved quotes');
    }
  }, []);

  const openAutoApprovedQuotes = useCallback(() => {
    setShowAutoApprovedQuotes(true);
    fetchAutoApprovedQuotes();
  }, [fetchAutoApprovedQuotes]);

  const fetchApprovalStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/quote-approval-stats`);
      setApprovalStats(response.data);
    } catch (error) {
      logger.error('Error fetching approval stats:', error);
    }
  }, []);

  // useEffect hooks - MUST be placed AFTER all useCallback functions they reference
  useEffect(() => {
    fetchDailySchedule();
    fetchWeeklySchedule();
    fetchPendingQuotes();
    fetchApprovalStats();
    fetchPendingPayments();
    fetchRecentCompleted();
    fetchAllJobs();
  }, [selectedDate, fetchDailySchedule, fetchWeeklySchedule, fetchPendingQuotes, fetchApprovalStats, fetchPendingPayments, fetchRecentCompleted, fetchAllJobs]);

  // Auto-refresh admin data every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchPendingPayments();
      fetchPendingQuotes();
      fetchApprovalStats();
      fetchDailySchedule();
      fetchRecentCompleted();
      fetchAllJobs();
    }, 30000);
    return () => clearInterval(interval);
  }, [selectedDate, fetchPendingPayments, fetchPendingQuotes, fetchApprovalStats, fetchDailySchedule, fetchRecentCompleted, fetchAllJobs]);

  useEffect(() => {
    if (showSmsCenter) {
      fetchSmsMessages();
    }
  }, [showSmsCenter, fetchSmsMessages]);

  useEffect(() => {
    if (showPhotoGallery) {
      fetchGalleryPhotos();
      fetchReelPhotos();
    }
  }, [showPhotoGallery, fetchGalleryPhotos, fetchReelPhotos]);

  const handleQuoteApproval = async (quoteId, action, adminNotes = '', approvedPrice = null) => {
    try {
      const response = await axios.post(`${API}/admin/quotes/${quoteId}/approve`, {
        action,
        admin_notes: adminNotes,
        approved_price: approvedPrice
      });
      
      toast.success(`Quote ${action}d successfully`);
      
      // Refresh data
      fetchPendingQuotes();
      fetchApprovalStats();
      
    } catch (error) {
      toast.error(`Failed to ${action} quote`);
    }
  };

  return (
    <FilterProvider>
    <div className="min-h-screen bg-gradient-to-br from-black/40 to-cyan-900/50 p-2 sm:p-4">
      <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6 overflow-visible">
        {/* Header — minimal, with live status */}
        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 sm:p-5 border border-white/20">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <img src="/text2toss-wordmark-nav.png?v=9" alt="Text2toss" className="h-10 w-auto drop-shadow-[0_4px_12px_rgba(34,211,238,0.35)]" />
              <div>
                <h1 className="text-xl sm:text-2xl font-display italic text-white uppercase tracking-tight leading-none">Admin</h1>
                {(() => {
                  // Live status line computed from allJobs (date-independent)
                  const todayStr = new Date().toISOString().split('T')[0];
                  const upcoming = allJobs
                    .filter(j => j.status === 'scheduled' && j.pickup_date)
                    .map(j => ({ ...j, _date: j.pickup_date.split('T')[0] }))
                    .sort((a, b) => (a._date + (a.pickup_time || '')).localeCompare(b._date + (b.pickup_time || '')));
                  const inProg = allJobs.filter(j => j.status === 'in_progress').length;
                  const todayJobs = upcoming.filter(j => j._date === todayStr);
                  const nextJob = upcoming.find(j => j._date >= todayStr);

                  if (inProg > 0) {
                    return (
                      <p className="text-cyan-300 text-xs sm:text-sm mt-1 font-display italic uppercase tracking-wider" data-testid="admin-status-line">
                        🚛 {inProg} job{inProg > 1 ? 's' : ''} in progress now
                      </p>
                    );
                  }
                  if (todayJobs.length > 0) {
                    return (
                      <p className="text-cyan-300 text-xs sm:text-sm mt-1 font-display italic uppercase tracking-wider" data-testid="admin-status-line">
                        📍 {todayJobs.length} pickup{todayJobs.length > 1 ? 's' : ''} today · Next at {todayJobs[0].pickup_time || 'TBD'}
                      </p>
                    );
                  }
                  if (nextJob) {
                    const d = new Date(nextJob._date + 'T00:00:00');
                    const dayLabel = d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
                    return (
                      <p className="text-cyan-300 text-xs sm:text-sm mt-1 font-display italic uppercase tracking-wider" data-testid="admin-status-line">
                        ⏭ Next pickup {dayLabel} · {nextJob.pickup_time || 'TBD'}
                      </p>
                    );
                  }
                  return (
                    <p className="text-white/60 text-xs sm:text-sm mt-1 font-display italic uppercase tracking-wider" data-testid="admin-status-line">
                      ✨ No pickups scheduled
                    </p>
                  );
                })()}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <ChimeToggle />
              <Button
                onClick={onLogout}
                size="sm"
                variant="outline"
                className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                data-testid="admin-logout-btn"
              >
                Logout
              </Button>
            </div>
          </div>
        </div>

        {/* === Quick Actions (primary entry point) === */}
        <QuickActionsGrid approvalStats={approvalStats} calculateOptimalRoute={calculateOptimalRoute} exportJobContacts={exportJobContacts} openAutoApprovedQuotes={openAutoApprovedQuotes} openCalendar={openCalendar} pendingQuotes={pendingQuotes} setShowPhotoGallery={setShowPhotoGallery} setShowQRModal={setShowQRModal} setShowQuoteApproval={setShowQuoteApproval} setShowReviewsModal={setShowReviewsModal} setShowSmsCenter={setShowSmsCenter} setShowSmsTestModal={setShowSmsTestModal} />
        {/* === Job Bins (compact glance row) === */}
        <JobBinsGrid bins={categorizBookings()} fetchPendingPayments={fetchPendingPayments} openAllJobsModal={openAllJobsModal} openBin={openBin} openCalendar={openCalendar} pendingPayments={pendingPayments} setShowEmailPreview={setShowEmailPreview} setShowInvoicesModal={setShowInvoicesModal} setShowPendingPayments={setShowPendingPayments} setShowPricingSettings={setShowPricingSettings} />
      </div>

      {/* Bin View Modal */}
      <BinModal
        open={!!selectedBin}
        selectedBin={selectedBin}
        binBookings={binBookings}
        jobs={allJobs}
        formatPrice={formatPrice}
        formatTime={formatTime}
        closeBin={closeBin}
        startRoute={startRoute}
        notifyCustomer={notifyCustomer}
        updateBookingStatus={updateBookingStatus}
        handleCompleteWithPhoto={handleCompleteWithPhoto}
        handleViewCustomerPhoto={handleViewCustomerPhoto}
        testSmsPhoto={testSmsPhoto}
      />

      <RouteOptimizerModal
        open={showRouteModal && !!selectedRouteBooking}
        booking={selectedRouteBooking}
        routeDirections={routeDirections}
        mapCenter={mapCenter}
        isLoaded={isLoaded}
        formatTime={formatTime}
        onClose={closeRouteModal}
      />

      {/* Completion Photo Modal */}
      <CompletionPhotoModal completionNote={completionNote} completionPhoto={completionPhoto} handleCompletionPhotoUpload={handleCompletionPhotoUpload} selectedBooking={selectedBooking} setCompletionNote={setCompletionNote} setShowCompletionModal={setShowCompletionModal} showCompletionModal={showCompletionModal} submitCompletion={submitCompletion} uploadingPhoto={uploadingPhoto} />
      {/* Calendar Modal */}
      <CalendarModal
        open={showCalendar}
        currentMonth={currentMonth}
        calendarData={calendarData}
        jobs={allJobs}
        selectedDate={selectedDate}
        formatPrice={formatPrice}
        formatCalendarDate={formatCalendarDate}
        getDaysInMonth={getDaysInMonth}
        getFirstDayOfWeek={getFirstDayOfWeek}
        changeMonth={changeMonth}
        closeCalendar={closeCalendar}
        openJobDetails={openJobDetails}
        setSelectedCalendarDate={setSelectedCalendarDate}
        setShowDateJobsModal={setShowDateJobsModal}
      />

      {/* Date Jobs Modal - Show all jobs for selected date */}
      {/* All Jobs Modal with Search */}
      <AllJobsModal
        open={showAllJobsModal}
        allJobs={allJobs}
        openJobDetails={openJobDetails}
        openEmailCenter={openEmailCenter}
        setShowAllJobsModal={setShowAllJobsModal}
        onJobUpdated={() => {
          fetchAllJobs();
          fetchDailySchedule?.();
          fetchPendingPayments?.();
        }}
      />

      {/* Live toast whenever the Venmo webhook auto-marks an invoice paid */}
      <VenmoPaidWatcher onPayment={() => { fetchDailySchedule?.(); fetchPendingPayments?.(); }} />

      {/* Admin Invoices Modal — edit any invoice's fields + line items */}
      <PricingSettingsModal open={showPricingSettings} onClose={() => setShowPricingSettings(false)} />
      <EmailPreviewModal open={showEmailPreview} onClose={() => setShowEmailPreview(false)} />
      <InvoicesModal
        open={showInvoicesModal}
        onClose={() => {
          setShowInvoicesModal(false);
          fetchDailySchedule?.();
          fetchPendingPayments?.();
        }}
      />

      {showDateJobsModal && selectedCalendarDate && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="text-2xl">
                    📅 Jobs for {new Date(selectedCalendarDate + 'T00:00:00').toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      month: 'long', 
                      day: 'numeric', 
                      year: 'numeric' 
                    })}
                  </CardTitle>
                  <CardDescription className="text-white/80 mt-1">
                    {(calendarData[selectedCalendarDate] || []).length} job(s) scheduled
                  </CardDescription>
                </div>
                <Button 
                  onClick={() => setShowDateJobsModal(false)}
                  variant="ghost"
                  className="text-white hover:bg-white/20"
                >
                  ✕
                </Button>
              </div>
            </CardHeader>
            
            <CardContent className="p-6 overflow-y-auto max-h-[70vh]">
              {(calendarData[selectedCalendarDate] || []).length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">📭</div>
                  <p className="text-gray-500 text-lg">No jobs scheduled for this date</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {(calendarData[selectedCalendarDate] || []).map((job, index) => (
                    <Card 
                      key={job.id}
                      className={`cursor-pointer hover:shadow-lg transition-all ${jobBorderClass(job.status)}`}
                      onClick={() => {
                        openJobDetails(job);
                        setShowDateJobsModal(false);
                      }}
                    >
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h3 className="font-bold text-lg text-gray-800">
                              Job #{index + 1} - {job.pickup_time}
                            </h3>
                            <p className="text-sm text-gray-600 mt-1">
                              ID: {job.id.substring(0, 8)}
                            </p>
                          </div>
                          <div className="text-right">
                            <div className="text-2xl font-bold text-emerald-600">
                              ${job.quote_details?.total_price || 0}
                            </div>
                            <Badge className={jobBadgeClass(job.status)}>
                              {jobBadgeLabel(job.status)}
                            </Badge>
                          </div>
                        </div>
                        
                        <div className="space-y-2 text-sm">
                          <div className="flex items-start gap-2">
                            <span className="font-semibold text-gray-700">📍 Address:</span>
                            <span className="text-gray-600">{job.address}</span>
                          </div>
                          <div className="flex items-start gap-2">
                            <span className="font-semibold text-gray-700">📞 Phone:</span>
                            <span className="text-gray-600">{job.phone}</span>
                          </div>
                          {job.special_instructions && (
                            <div className="flex items-start gap-2">
                              <span className="font-semibold text-gray-700">📝 Notes:</span>
                              <span className="text-gray-600">{job.special_instructions}</span>
                            </div>
                          )}
                        </div>
                        
                        <div className="mt-3 pt-3 border-t border-gray-300">
                          <p className="text-xs text-gray-500 text-center">
                            Click to view full details
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
              
              <div className="mt-6 text-center">
                <Button
                  onClick={() => setShowDateJobsModal(false)}
                  variant="outline"
                  className="px-8"
                >
                  Close
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Email Notification Center Modal */}
      <EmailCenterModal
        open={showSmsCenter}
        emailCompose={emailCompose}
        setEmailCompose={setEmailCompose}
        sendingEmail={sendingEmail}
        sendCustomEmail={sendCustomEmail}
        sendBulkEmailReminder={sendBulkEmailReminder}
        setShowSmsCenter={setShowSmsCenter}
      />

      <PendingApprovalsModal
        open={showQuoteApproval}
        pendingQuotes={pendingQuotes}
        approvalStats={approvalStats}
        failedQuoteImages={failedQuoteImages}
        onMarkImageFailed={(id) => setFailedQuoteImages(prev => new Set([...prev, id]))}
        onApprove={(id, notes, price) => handleQuoteApproval(id, 'approve', notes, price)}
        onReject={(id, notes) => handleQuoteApproval(id, 'reject', notes)}
        onClose={() => setShowQuoteApproval(false)}
      />

      <AutoApprovedQuotesModal
        open={showAutoApprovedQuotes}
        quotes={autoApprovedQuotes}
        loading={autoApprovedLoading}
        onClose={() => setShowAutoApprovedQuotes(false)}
        onRefresh={fetchAutoApprovedQuotes}
        onDismissQuote={handleDismissAutoApproved}
        onDismissAll={handleDismissAllAutoApproved}
      />

      {/* Photo Gallery Management Modal */}
      <PhotoGalleryModal
        open={showPhotoGallery}
        galleryPhotos={galleryPhotos}
        reelPhotos={reelPhotos}
        uploadGalleryPhoto={uploadGalleryPhoto}
        removeGalleryPhoto={removeGalleryPhoto}
        updateReelPhoto={updateReelPhoto}
        setShowPhotoGallery={setShowPhotoGallery}
        cleaningBroken={cleaningBroken}
        cleanupBrokenPhotos={cleanupBrokenPhotos}
      />

      {/* Customer Photo Viewing Modal */}
      {showCustomerPhoto && currentCustomerPhoto && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4">
          <Card className="w-full max-w-2xl mx-2 sm:mx-0 max-h-[90vh] overflow-y-auto">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg sm:text-xl flex items-center gap-2">
                <span className="text-xl">📷</span>
                Customer Uploaded Photo
              </CardTitle>
              <CardDescription className="text-sm space-y-1">
                <div className="font-medium text-gray-700">Booking ID: {currentCustomerPhoto.booking_id?.slice(0, 8)}...</div>
                <div className="text-gray-600">📍 {currentCustomerPhoto.address}</div>
                <div className="text-gray-600">📅 {new Date(currentCustomerPhoto.pickup_date).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric', 
                  month: 'long',
                  day: 'numeric'
                })}</div>
                <div className="text-gray-600">📱 {currentCustomerPhoto.customer_phone}</div>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Photo Display */}
              <div className="relative">
                <img 
                  src={currentCustomerPhoto.url} 
                  alt="Customer uploaded photo" 
                  className="w-full max-h-[400px] object-contain rounded-lg border-2 border-gray-200 bg-gray-50"
                  onError={(e) => {
                    e.target.src = '/placeholder-image.png';
                    e.target.alt = 'Photo could not be loaded';
                  }}
                />
                <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                  Original Size
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
                <Button
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = currentCustomerPhoto.url;
                    link.download = `customer-photo-${currentCustomerPhoto.booking_id?.slice(0, 8)}.jpg`;
                    link.target = '_blank';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                  className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white"
                >
                  <span className="mr-2">⬇️</span>
                  Download Photo
                </Button>
                <Button
                  onClick={() => {
                    window.open(currentCustomerPhoto.url, '_blank');
                  }}
                  variant="outline"
                  className="flex-1 border-green-400 text-green-700 hover:bg-green-50"
                >
                  <span className="mr-2">🔍</span>
                  Open Full Size
                </Button>
                <Button
                  onClick={() => {
                    setShowCustomerPhoto(false);
                    setCurrentCustomerPhoto(null);
                  }}
                  variant="outline"
                  className="flex-1 border-gray-400 text-gray-700 hover:bg-gray-50"
                >
                  <span className="mr-2">✕</span>
                  Close
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <PaymentRemindersModal
        open={showPendingPayments}
        pendingPayments={pendingPayments}
        onClose={() => setShowPendingPayments(false)}
        onMarkPaid={markAsPaid}
        onReject={rejectPayment}
        onRejectAll={rejectAllPendingPayments}
        onQuoteUpdated={() => {
          fetchPendingPayments();
          fetchDailySchedule?.();
          fetchAllJobs?.();
        }}
      />

      <MarketingQRModal open={showQRModal} onClose={() => setShowQRModal(false)} />
      <ReviewsModal open={showReviewsModal} onClose={() => setShowReviewsModal(false)} />
      <SmsTestModal open={showSmsTestModal} onClose={() => setShowSmsTestModal(false)} />
    </div>
    </FilterProvider>
  );
};

export default AdminDashboard;